import { Link } from 'react-router-dom';
import { ArrowRight, Check, Lock, Rocket, Sprout, Waves, type LucideIcon } from 'lucide-react';
import {
  LEARNING_PATHS,
  PATH_INFO,
  type LearningPath,
  type PathStep,
  type PublicPathItem,
} from '@ready/shared';
import type { CatalogItem, LibraryEvent } from '@/lib/api';
import { formatDate } from '@/lib/dates';
import { formatMinutes } from './catalog';
import { Button } from './ui';

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ');

/*
 * Learning paths (`@ready/shared` paths.ts): the three path cards (landing,
 * /paths, Home), a path's timeline, its upcoming events and "Find more".
 */

export const PATH_ICONS: Record<LearningPath, LucideIcon> = {
  'first-steps': Sprout,
  'level-up': Rocket,
  'deep-end': Waves,
};

/** Explore, filtered to the path's level: the "Find more" link. */
export const findMoreHref = (path: LearningPath) => `/learn?level=${PATH_INFO[path].levels[0]}`;

export function PathCards({
  current,
  onChoose,
  choosing,
}: {
  /** The learner's path, marked "Your path". */
  current?: LearningPath | null;
  /** Shows "This is me" on each card (Home). */
  onChoose?: (path: LearningPath) => void;
  choosing?: boolean;
}) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      {LEARNING_PATHS.map((id) => {
        const p = PATH_INFO[id];
        const Icon = PATH_ICONS[id];
        const mine = current === id;
        return (
          <div
            key={id}
            className={cx(
              'flex flex-col rounded-2xl border bg-card p-5',
              mine ? 'border-primary' : 'border-border',
            )}
          >
            <div className="mb-3 flex items-center justify-between gap-2">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-muted">
                <Icon className="h-5 w-5 text-primary" aria-hidden />
              </span>
              {mine && (
                <span className="rounded-full bg-primary/10 px-2 py-1 text-[11px] font-semibold text-primary">
                  Your path
                </span>
              )}
            </div>
            <h3 className="font-display text-lg font-semibold">{p.name}</h3>
            <p className="text-sm font-medium text-muted-foreground">{p.tagline}</p>
            <p className="mt-3 text-sm leading-relaxed">{p.definition}</p>
            <p className="mt-3 rounded-lg bg-muted/60 p-3 text-sm leading-relaxed text-muted-foreground">
              <span className="font-semibold text-foreground">This is you if… </span>“{p.example}”
            </p>
            <div className="mt-auto flex flex-col gap-2 pt-4 sm:flex-row sm:items-center">
              {onChoose && !mine && (
                <Button
                  className="w-full sm:w-auto"
                  disabled={choosing}
                  onClick={() => onChoose(id)}
                >
                  This is me
                </Button>
              )}
              <Link
                to={`/paths/${id}`}
                className="-my-2 inline-flex items-center gap-1 py-2 text-sm font-semibold text-primary"
              >
                See the path <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </div>
          </div>
        );
      })}
    </div>
  );
}

type TimelineItem = PublicPathItem | CatalogItem;

const isLearnerItem = (i: TimelineItem): i is CatalogItem => 'status' in i;

function stepFor(path: LearningPath, slug: string): PathStep | undefined {
  return PATH_INFO[path].timeline.find((s) => s.slug === slug);
}

function facts(i: TimelineItem) {
  return [
    i.kind === 'quiz' ? 'Quiz' : 'Course',
    i.lessonCount ? `${i.lessonCount} lessons` : null,
    i.kind === 'quiz' && i.questionCount ? `${i.questionCount} questions` : null,
    i.minutes ? formatMinutes(i.minutes) : null,
  ].filter(Boolean);
}

/** The path as a timeline: every item in order, with progress when signed in. */
export function PathTimeline({
  path,
  items,
  next,
}: {
  path: LearningPath;
  items: TimelineItem[];
  /** The learner's next step, highlighted. */
  next?: string | null;
}) {
  return (
    <ol className="relative">
      {items.map((item, index) => {
        const step = stepFor(path, item.slug);
        const done = isLearnerItem(item) && item.status === 'completed';
        const started = isLearnerItem(item) && item.status === 'in_progress';
        const isNext = next === item.slug;
        const last = index === items.length - 1;
        return (
          <li key={`${item.kind}-${item.slug}`} className="relative flex gap-4 pb-6">
            {!last && (
              <span
                className="absolute top-9 left-[17px] h-[calc(100%-2.25rem)] w-px bg-border"
                aria-hidden
              />
            )}
            <span
              className={cx(
                'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-sm font-semibold',
                done
                  ? 'border-primary bg-primary text-primary-foreground'
                  : isNext
                    ? 'border-primary text-primary'
                    : 'border-border bg-card text-muted-foreground',
              )}
            >
              {done ? <Check className="h-4 w-4" aria-label="Done" /> : index + 1}
            </span>
            <div
              className={cx(
                'min-w-0 flex-1 rounded-xl border p-4',
                isNext ? 'border-primary bg-primary/5' : 'border-border bg-card',
              )}
            >
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                {facts(item).join(' · ')}
                {isNext && (
                  <span className="rounded-full bg-primary px-2 py-0.5 font-semibold text-primary-foreground">
                    Next
                  </span>
                )}
                {started && !isNext && (
                  <span className="font-semibold text-primary">In progress</span>
                )}
                {done && <span className="font-semibold text-primary">Done</span>}
              </div>
              <Link
                to={item.kind === 'quiz' ? `/q/${item.slug}` : `/c/${item.slug}`}
                className="-my-1 mt-1 block py-1 font-display text-base font-semibold hover:text-primary"
              >
                {item.title}
              </Link>
              {step && <p className="text-sm text-muted-foreground">{step.why}</p>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** "Find more" into Explore, filtered to the path's level. */
export function FindMore({ path }: { path: LearningPath }) {
  const level = PATH_INFO[path].levels[0];
  return (
    <Link
      to={findMoreHref(path)}
      className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-border bg-card px-4 py-3 text-sm font-semibold hover:border-primary sm:w-auto"
    >
      Find more {level} courses, quizzes and events in Explore{' '}
      <ArrowRight className="h-4 w-4" aria-hidden />
    </Link>
  );
}

/** Deep End's courses offered on request. */
export function RestrictedNote({ path }: { path: LearningPath }) {
  const r = PATH_INFO[path].restricted;
  if (!r) return null;
  return (
    <div className="flex gap-3 rounded-xl border border-dashed border-border p-4 text-sm">
      <Lock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      <p>
        <span className="font-semibold">{r.title}</span> (restricted). {r.description} To request
        access, email{' '}
        <a
          href={`mailto:${r.contact}`}
          className="-my-2 inline-block py-2 font-semibold break-all text-primary underline underline-offset-2"
        >
          {r.contact}
        </a>
        .
      </p>
    </div>
  );
}

/** Upcoming public workshops and webinars at the path's levels. */
export function PathEvents({ events }: { events: LibraryEvent[] }) {
  if (!events.length)
    return (
      <p className="text-sm text-muted-foreground">
        No workshops or webinars scheduled at this level right now. New ones appear here and in
        Explore.
      </p>
    );
  return (
    <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {events.map((e) => (
        <li key={e.slug}>
          <Link
            to={`/e/${e.slug}`}
            className="block rounded-xl border border-border bg-card p-4 hover:border-primary"
          >
            <p className="text-xs text-muted-foreground">
              {e.kind === 'webinar' ? 'Webinar' : 'Workshop'} · {formatDate(e.nextStartsAt)} ·{' '}
              {e.hostName}
            </p>
            <p className="mt-1 font-semibold">{e.title}</p>
          </Link>
        </li>
      ))}
    </ul>
  );
}
